import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAnalysis } from '@/lib/analyses/registry';
import type { RunFilters, RunStatusFilter } from '@/lib/analyses/runList';
import { translateKey } from '@/lib/analyses/translateKey';
import type { AnalysisId } from '@/lib/analyses/types';
import { cn } from '@/lib/utils';

const ALL_TYPES = '__all__';
const STATUSES = ['running', 'failed'] as const;

interface Props {
  filters: RunFilters;
  onChange: (next: RunFilters) => void;
  counts: Record<RunStatusFilter, number>;
  types: AnalysisId[];
}

/** Search · status chips · analysis type. Status chips hide at a zero count
 *  unless selected; clicking the selected chip clears the status filter.
 *  The type select appears once there are two types to choose between. */
export function AnalysisRunToolbar({ filters, onChange, counts, types }: Props) {
  const { t } = useTranslation('project');
  const visibleStatuses = STATUSES.filter((status) => counts[status] > 0 || filters.status === status);
  return (
    <div className='flex flex-wrap items-center gap-2' role='search' aria-label={t('analyses.filters.label')}>
      <div className='relative w-full min-w-0 sm:w-auto sm:min-w-48 sm:flex-1'>
        <Search
          className='pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-stone-500'
          aria-hidden
        />
        <Input
          data-testid='analysis-runs-search'
          type='search'
          value={filters.query}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
          placeholder={t('analyses.searchPlaceholder')}
          aria-label={t('analyses.searchPlaceholder')}
          className='h-9 bg-background pl-8 pr-8 [&::-webkit-search-cancel-button]:hidden'
        />
        {filters.query && (
          <button
            type='button'
            data-testid='analysis-runs-clear-search'
            aria-label={t('analyses.clearSearch')}
            className='absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 outline-none hover:text-slate-700 focus-visible:text-slate-700 focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:text-stone-500 dark:hover:text-stone-200 dark:focus-visible:text-stone-200'
            onClick={() => onChange({ ...filters, query: '' })}
          >
            <X className='size-3.5' />
          </button>
        )}
      </div>
      {visibleStatuses.length > 0 && (
        <div className='flex items-center gap-1'>
          {visibleStatuses.map((status) => {
            const selected = filters.status === status;
            return (
              <button
                key={status}
                type='button'
                data-testid={`analysis-runs-status-${status}`}
                aria-pressed={selected}
                onClick={() => onChange({ ...filters, status: selected ? 'all' : status })}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  selected
                    ? 'border-slate-900 bg-slate-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300 dark:hover:bg-stone-800',
                )}
              >
                {t(`analyses.filters.${status}`)}
                <span
                  className={cn(
                    'tabular-nums',
                    selected ? 'text-white/70 dark:text-stone-900/60' : 'text-slate-400 dark:text-stone-500',
                    status === 'failed' && !selected && 'text-rose-500 dark:text-rose-400',
                  )}
                >
                  {counts[status]}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {types.length > 1 && (
        <Select
          value={filters.analysisId ?? ALL_TYPES}
          onValueChange={(v) => onChange({ ...filters, analysisId: v === ALL_TYPES ? null : (v as AnalysisId) })}
        >
          <SelectTrigger
            data-testid='analysis-runs-type'
            aria-label={t('analyses.filters.type')}
            className='w-full min-w-0 bg-background sm:w-auto sm:min-w-40'
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES} data-testid='analysis-runs-type-all'>
              {t('analyses.filters.allTypes')}
            </SelectItem>
            {types.map((id) => (
              <SelectItem key={id} value={id} data-testid={`analysis-runs-type-${id}`}>
                {translateKey(t, getAnalysis(id).titleKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
